import { createPublicClient, decodeErrorResult, http, type Address, type Hex } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { arbitrum, arbitrumSepolia } from 'viem/chains';
import {
  entryPoint07Address,
  WaitForUserOperationReceiptTimeoutError,
} from 'viem/account-abstraction';
import { createSmartAccountClient } from 'permissionless';
import { createPimlicoClient } from 'permissionless/clients/pimlico';
import { toSafeSmartAccount as toLightSmartAccount } from 'permissionless/accounts';
import type { EncodedTx, SubmissionResult } from '../types.js';
import type { OstiumClientConfig } from '../config.js';
import type { SubmissionStrategy } from './index.js';
import { OstiumError, OstiumErrorCode, OstiumSubmissionPendingError } from '../errors.js';
import { TRADING_ABI } from '../internal/contract.js';

/**
 * Is this URL one of Ostium's sponsorship endpoints?
 *
 * Decides which of the two submission flows runs. It is a property of the
 * endpoint, not of how the caller configured the client: someone who passes
 * the Ostium URL explicitly should get the sponsored flow, and someone who
 * points `pimlicoUrl` at their own Pimlico project should get the classic one.
 *
 * Matched on host + path rather than compared to the default constants so a
 * staging or self-hosted deployment of the same service still resolves to the
 * sponsored flow.
 */
export function isOstiumSponsorshipEndpoint(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url);
    return (
      (hostname === 'ostium.io' || hostname.endsWith('.ostium.io')) &&
      pathname.includes('/sponsor')
    );
  } catch {
    // Not a URL we can parse — treat it as third-party and take the classic
    // flow, which pays real fees rather than assuming someone sponsors them.
    return false;
  }
}

/**
 * UserOperation gas limits for sponsored submission.
 *
 * The sponsorship endpoint applies the paymaster upstream, so operations are
 * submitted at a zero fee — and bundlers refuse to run
 * `eth_estimateUserOperationGas` at a zero `maxFeePerGas`. Limits are therefore
 * supplied rather than estimated, matching the Ostium frontend. Unused gas is
 * refunded, so headroom costs nothing.
 *
 * `preVerificationGas: 0n` is the exception, and the constant that makes this
 * submitter Ostium-relay-only rather than merely Ostium-relay-tuned. On
 * Arbitrum it covers the L1 calldata cost of posting the batch, so zero is not
 * headroom — it asks the bundler to absorb that cost outright. Confirmed
 * on-chain: userOps through this path settle with `actualGasCost = 0` and a
 * zero-address paymaster. Point this at a bundler that does not sponsor and
 * this is the first constant to change.
 *
 * ponytail: fixed limits; make them configurable if a call ever exceeds them.
 */
const SPONSORED_CALL_GAS_LIMIT = 750_000n;
const SPONSORED_VERIFICATION_GAS_LIMIT = 350_000n;
const SPONSORED_PRE_VERIFICATION_GAS = 0n;

/**
 * Poll the bundler this often while waiting for the receipt.
 *
 * The frontend polls at 100-200ms, which suits one user watching one trade in
 * a browser. This SDK can have many submissions in flight against a shared,
 * rate-limited sponsorship endpoint, so it polls more gently. A market order
 * takes seconds to fill; 500ms costs nothing visible and avoids being the
 * client that trips the limit for everyone.
 */
const RECEIPT_POLL_INTERVAL_MS = 500;

/**
 * Give up waiting for a receipt after this long.
 *
 * Without it a dropped or never-bundled operation leaves the caller awaiting
 * forever, indistinguishable from a slow fill.
 */
const RECEIPT_TIMEOUT_MS = 120_000;

/**
 * Turn a failed UserOperation receipt into a typed error.
 *
 * A UserOperation that reverts still lands on-chain as a successful
 * transaction — the EntryPoint catches the inner revert and emits
 * `UserOperationRevertReason`. Only `receipt.success` distinguishes the two, so
 * a submitter that stops at the transaction hash reports a reverted trade as a
 * success. `reason` carries the raw revert data, which decodes against the
 * trading ABI into the actual contract error.
 */
function userOperationRevertError(receipt: { reason?: unknown; receipt?: { transactionHash?: Hex } }): OstiumError {
  const txHash = receipt.receipt?.transactionHash;
  const suffix = txHash ? ` (tx ${txHash})` : '';

  const reason = receipt.reason;
  if (typeof reason === 'string' && reason.startsWith('0x') && reason.length > 2) {
    try {
      const decoded = decodeErrorResult({ abi: TRADING_ABI, data: reason as Hex });
      return new OstiumError(
        `Contract reverted: ${decoded.errorName}${suffix}`,
        OstiumErrorCode.CONTRACT_ERROR,
      );
    } catch {
      // Not a trading-contract error — surface the raw data rather than nothing.
      return new OstiumError(
        `UserOperation reverted with data ${reason}${suffix}`,
        OstiumErrorCode.CONTRACT_ERROR,
      );
    }
  }

  return new OstiumError(
    `UserOperation reverted on-chain${suffix}`,
    OstiumErrorCode.CONTRACT_ERROR,
  );
}

/**
 * ERC-4337 failure codes (`AA23 reverted`, `AA41 too little verificationGas`…).
 *
 * Matched as a whole token, never as a substring: `a` is a hex digit, so any
 * error carrying revert data or an address contains `aa` somewhere and a naive
 * `includes('aa')` labels every one of them a contract revert.
 */
const AA_ERROR_CODE_RE = /\bAA\d{2}\b/i;

/** @internal Exported for tests; not part of the public API. */
export function classifyGaslessError(err: unknown): OstiumError {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();

  if (
    lower.includes('execution reverted') ||
    lower.includes('reverted with reason') ||
    lower.includes('revert') ||
    lower.includes('userop reverted') ||
    AA_ERROR_CODE_RE.test(msg)
  ) {
    return new OstiumError(`Contract reverted: ${msg}`, OstiumErrorCode.CONTRACT_ERROR, err);
  }

  if (lower.includes('429') || lower.includes('rate limit') || lower.includes('too many requests')) {
    return new OstiumError(
      `Pimlico rate limit exceeded — retry after 60 seconds. ${msg}`,
      OstiumErrorCode.NETWORK_ERROR,
      err,
    );
  }

  if (lower.includes('sponsorship') || lower.includes('policy')) {
    return new OstiumError(
      `Pimlico sponsorship policy error — check sponsorshipPolicyId config. ${msg}`,
      OstiumErrorCode.INVALID_CONFIG,
      err,
    );
  }

  if (
    lower.includes('fetch') ||
    lower.includes('etimedout') ||
    lower.includes('econnrefused') ||
    lower.includes('network') ||
    lower.includes('timeout') ||
    lower.includes('bundle') ||
    lower.includes('connection')
  ) {
    return new OstiumError(`Network error: ${msg}`, OstiumErrorCode.NETWORK_ERROR, err);
  }

  return new OstiumError(
    `Gasless submission failed: ${msg}`,
    OstiumErrorCode.SUBMISSION_FAILED,
    err,
  );
}

/**
 * Gasless submission strategy — submits transactions via Pimlico as ERC-4337
 * UserOperations signed by a Safe 1.4.1 smart account.
 *
 * The Safe address is the submitter's effectiveSender. In Self + Gasless mode
 * the Safe address also becomes the trader's on-chain identity (trade.trader).
 *
 * Use the static GaslessSubmissionStrategy.create() factory to initialise —
 * it resolves the Safe address before returning, so effectiveSender is always
 * available synchronously after construction.
 */
export class GaslessSubmissionStrategy implements SubmissionStrategy {
  readonly effectiveSender: Address;
  private readonly testnet: boolean;
  private readonly pimlicoUrl: string;
  private readonly sponsorshipPolicyId?: string;
  /** True when `pimlicoUrl` is an Ostium sponsorship endpoint. See `submit`. */
  private readonly sponsoredEndpoint: boolean;
  // Cached Safe account — derived once in create(), reused on every submit().
  private readonly safeAccount: Awaited<ReturnType<typeof toLightSmartAccount>>;

  private constructor(
    testnet: boolean,
    pimlicoUrl: string,
    sponsorshipPolicyId: string | undefined,
    safeAccount: Awaited<ReturnType<typeof toLightSmartAccount>>,
  ) {
    this.testnet = testnet;
    this.pimlicoUrl = pimlicoUrl;
    this.sponsorshipPolicyId = sponsorshipPolicyId;
    // Sponsored flow only when nothing else has to be carried in a
    // paymasterContext. A caller with a policy id keeps the paymaster-attached
    // flow: it is what they were already running, and it is the only way their
    // policy reaches the paymaster at all.
    this.sponsoredEndpoint =
      isOstiumSponsorshipEndpoint(pimlicoUrl) && !sponsorshipPolicyId;
    this.safeAccount = safeAccount;
    this.effectiveSender = safeAccount.address;
  }

  static async create(
    config: OstiumClientConfig,
    testnet: boolean,
  ): Promise<GaslessSubmissionStrategy> {
    if (!config.privateKey) {
      throw new OstiumError(
        'privateKey is required for gasless submission mode',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    if (!config.pimlicoUrl) {
      throw new OstiumError(
        'pimlicoUrl is required for gasless mode',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }

    const chain = testnet ? arbitrumSepolia : arbitrum;
    const pimlicoUrl = config.pimlicoUrl;

    const chainRpc =
      config.rpcUrl ??
      (testnet ? 'https://sepolia-rollup.arbitrum.io/rpc' : 'https://arb1.arbitrum.io/rpc');
    const rpcTransport = http(chainRpc);
    const publicClient = createPublicClient({ chain, transport: rpcTransport });
    const owner = privateKeyToAccount(config.privateKey);

    try {
      const safeAccount = await toLightSmartAccount({
        client: publicClient,
        owners: [owner],
        entryPoint: {
          address: entryPoint07Address,
          version: '0.7',
        },
        version: '1.4.1',
        // A caller-supplied address skips permissionless's on-chain
        // counterfactual-address derivation (one eth_call per construction).
        address: config.safeAddress,
      });

      return new GaslessSubmissionStrategy(testnet, pimlicoUrl, config.sponsorshipPolicyId, safeAccount);
    } catch (err) {
      throw new OstiumError(
        `Failed to derive Safe smart account address: ${err instanceof Error ? err.message : String(err)}`,
        OstiumErrorCode.INVALID_CONFIG,
        err,
      );
    }
  }

  /**
   * Submit as an ERC-4337 UserOperation.
   *
   * Two flows, chosen by the endpoint:
   *
   * - **Ostium sponsorship endpoint** (the default). It applies the paymaster
   *   upstream, so no paymaster client is attached, fees are zero, and gas
   *   limits are supplied rather than estimated — a bundler will not estimate
   *   at a zero `maxFeePerGas`.
   * - **Your own Pimlico** (`pimlicoUrl` set to a non-Ostium URL). The classic
   *   flow: a Pimlico paymaster client carrying your `sponsorshipPolicyId`,
   *   Pimlico's own fee quote, and bundler-estimated gas limits. Nothing
   *   sponsors these operations for free, so the zero-fee shortcut above would
   *   simply be rejected.
   */
  /**
   * Classic Pimlico flow — a paymaster client with the caller's policy and
   * Pimlico's fee quote. Used when `pimlicoUrl` is not an Ostium endpoint.
   */
  private createSelfSponsoredClient(chain: typeof arbitrum | typeof arbitrumSepolia) {
    const pimlicoClient = createPimlicoClient({
      transport: http(this.pimlicoUrl),
      entryPoint: { address: entryPoint07Address, version: '0.7' },
    });

    return createSmartAccountClient({
      account: this.safeAccount,
      chain,
      bundlerTransport: http(this.pimlicoUrl),
      paymaster: pimlicoClient,
      userOperation: {
        estimateFeesPerGas: async () => (await pimlicoClient.getUserOperationGasPrice()).fast,
      },
      pollingInterval: RECEIPT_POLL_INTERVAL_MS,
      ...(this.sponsorshipPolicyId && {
        paymasterContext: { sponsorshipPolicyId: this.sponsorshipPolicyId },
      }),
    });
  }

  async submit(encoded: EncodedTx): Promise<SubmissionResult> {
    const chain = this.testnet ? arbitrumSepolia : arbitrum;
    const call = { to: encoded.to, data: encoded.data, value: encoded.value ?? 0n };

    try {
      const smartAccountClient = this.sponsoredEndpoint
        ? createSmartAccountClient({
            account: this.safeAccount,
            chain,
            bundlerTransport: http(this.pimlicoUrl),
            userOperation: {
              estimateFeesPerGas: async () => ({
                maxFeePerGas: 0n,
                maxPriorityFeePerGas: 0n,
              }),
            },
            pollingInterval: RECEIPT_POLL_INTERVAL_MS,
          })
        : this.createSelfSponsoredClient(chain);

      // Send and await the UserOperation rather than using `sendTransaction`.
      // `sendTransaction` resolves on the transaction receipt, which is
      // successful even when the inner call reverted — the EntryPoint catches
      // the revert. Only the UserOperation receipt carries `success`.
      const hash = await smartAccountClient.sendUserOperation({
        calls: [call],
        // Fixed limits only where the relay absorbs the cost. On a
        // caller-owned bundler the operation is paid for, so let it estimate.
        ...(this.sponsoredEndpoint
          ? {
              callGasLimit: SPONSORED_CALL_GAS_LIMIT,
              verificationGasLimit: SPONSORED_VERIFICATION_GAS_LIMIT,
              preVerificationGas: SPONSORED_PRE_VERIFICATION_GAS,
            }
          : {}),
      });

      let receipt;
      try {
        receipt = await smartAccountClient.waitForUserOperationReceipt({
          hash,
          pollingInterval: RECEIPT_POLL_INTERVAL_MS,
          timeout: RECEIPT_TIMEOUT_MS,
        });
      } catch (err) {
        // The bundler already accepted this operation; giving up on the
        // receipt does not cancel it, and it can still land. Surfacing a
        // generic failure here would invite a retry and open the position
        // twice, so hand back the hash and say so explicitly.
        if (err instanceof WaitForUserOperationReceiptTimeoutError) {
          throw new OstiumSubmissionPendingError(
            `UserOperation ${hash} was accepted by the bundler but produced no receipt within ` +
              `${RECEIPT_TIMEOUT_MS / 1000}s. It may still execute — reconcile against ` +
              `getOpenPositions() or getOrders() before resubmitting.`,
            hash,
            err,
          );
        }
        throw err;
      }

      if (!receipt.success) {
        throw userOperationRevertError(receipt);
      }

      return {
        txHash: receipt.receipt.transactionHash,
        smartAccountAddress: this.safeAccount.address,
      };
    } catch (err) {
      // Already typed — a revert classified from the receipt must not be
      // reclassified by the generic string matcher.
      if (err instanceof OstiumError) throw err;
      throw classifyGaslessError(err);
    }
  }
}
